/**
 * CapacitySplitBar.tsx
 * The SPLIT AVAILABILITY BAR for one (person, week) capacity cell (redesign
 * 2026-07-14, SPEC.md §2). Four segments + a "C +T +mine" caption + a
 * "N free" / "+N over" badge:
 *   - Committed   — firm, live-schedule bookings (solid, theme.schedule.committed).
 *   - Uncommitted — tentative, other-draft-plan load (amber hatch,
 *                   theme.schedule.capacityUncommitted).
 *   - This project — the editable slice (theme.schedule.machineTime, slate blue —
 *                   reused per the redesign brief, distinct from the other two).
 *   - Free         — the light remainder.
 * Clicking the bar opens the breakdown popover (CapacityCellPopover). Colors
 * via theme tokens only — no hex literals (CODING-STANDARDS).
 */

import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  buildCapacitySplit,
  splitBarWidths,
  capacityFreeColorIntent,
  DEFAULT_SPLIT_VISIBILITY,
  type SplitVisibility,
} from './capacitySplitModel';

export interface CapacitySplitBarProps {
  committedPercent: number;
  uncommittedPercent: number;
  minePercent: number;
  visibility?: SplitVisibility;
  /** Show the "C +T +mine / N free" caption row under the bar (off for read-only future cells). */
  showCaption?: boolean;
  onOpenDetail?: (e: React.MouseEvent<HTMLElement>) => void;
}

export const CapacitySplitBar: React.FC<CapacitySplitBarProps> = ({
  committedPercent,
  uncommittedPercent,
  minePercent,
  visibility = DEFAULT_SPLIT_VISIBILITY,
  showCaption = true,
  onOpenDetail,
}) => {
  const theme = useTheme();
  const split = buildCapacitySplit(committedPercent, uncommittedPercent, minePercent);
  const w = splitBarWidths(split, visibility);
  const intent = capacityFreeColorIntent(split);
  const freeColor =
    intent === 'over' ? theme.schedule.allocOver
      : intent === 'healthy' ? theme.schedule.committed
        : theme.schedule.capacityUncommitted;

  const uncommittedFill = `repeating-linear-gradient(45deg, ${theme.schedule.capacityUncommitted}, ${theme.schedule.capacityUncommitted} 3px, ${alpha(theme.schedule.capacityUncommitted, 0.4)} 3px, ${alpha(theme.schedule.capacityUncommitted, 0.4)} 6px)`;

  const clickable = Boolean(onOpenDetail);
  const tip = `Committed ${split.committed}% · Uncommitted ${split.uncommitted}% · This project ${split.mine}%`
    + (split.over > 0 ? ` · ${split.over}% over` : ` · ${split.free}% free`);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, width: '100%' }}>
      <Tooltip title={tip} arrow placement="top">
        <Box
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={onOpenDetail}
          onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenDetail?.(e as unknown as React.MouseEvent<HTMLElement>); } } : undefined}
          sx={{
            display: 'flex',
            height: 12,
            width: '100%',
            borderRadius: '6px',
            overflow: 'hidden',
            border: `1px solid ${theme.border.color}`,
            backgroundColor: alpha(theme.palette.text.disabled, 0.08),
            cursor: clickable ? 'pointer' : 'default',
          }}
        >
          {w.committed > 0 && <Box sx={{ width: `${w.committed}%`, backgroundColor: theme.schedule.committed }} />}
          {w.uncommitted > 0 && <Box sx={{ width: `${w.uncommitted}%`, backgroundImage: uncommittedFill }} />}
          {w.mine > 0 && <Box sx={{ width: `${w.mine}%`, backgroundColor: theme.schedule.machineTime }} />}
          {w.free > 0 && <Box sx={{ width: `${w.free}%`, backgroundColor: 'transparent' }} />}
          {split.over > 0 && <Box sx={{ width: 6, flexShrink: 0, backgroundColor: theme.schedule.allocOver }} />}
        </Box>
      </Tooltip>

      {showCaption && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.4 }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'text.secondary', lineHeight: 1.2 }}>
            {visibility.committed && (
              <Box component="span" sx={{ color: theme.schedule.committed }}>{split.committed}</Box>
            )}
            {visibility.uncommitted && split.uncommitted > 0 && (
              <Box component="span"> +<Box component="span" sx={{ color: theme.schedule.capacityUncommitted }}>{split.uncommitted}</Box></Box>
            )}
            {split.mine > 0 && (
              <Box component="span"> +<Box component="span" sx={{ color: theme.schedule.machineTime }}>{split.mine}</Box></Box>
            )}
          </Typography>
          {visibility.free && (
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: freeColor, lineHeight: 1.2, flexShrink: 0 }}>
              {split.over > 0 ? `+${split.over} over` : `${split.free} free`}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
